import { useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'

import { useAuth } from '../auth/AuthProvider'
import { useCart } from '../cart/CartProvider'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { useMarketShellScrollLock } from '../hooks/useMarketShellScrollLock'
import { useOrganizationAccess } from '../hooks/useOrganizationAccess'
import { usePlanGate, type PlanGateFeature } from '../hooks/usePlanGate'
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed'
import { VenueTabs } from '../venues/VenueTabs'
import { DemoStandBanner } from '../dev/DemoStandBanner'
import { DemoUiBanner } from '../dev/DemoUiBanner'
import { DEMO_STAND_REGISTER_URL, isDemoStandEnabled } from '../dev/demoStand'
import type { UserRole } from '../api/types'
import { NavIcon, type NavIconName } from './NavIcon'
import { NotificationsBellPanel } from './NotificationsBellPanel'
import { RfqNotificationsProvider } from '../notifications/RfqNotificationsProvider'
import { SelectCombobox } from './SelectCombobox'
import { IconLogout } from './icons/CatalogActionIcons'
import { MenuIcon } from './icons/MenuIcon'
import {
  IconChevronLeft,
  IconChevronRight,
  IconSidebarCollapse,
  IconSidebarExpand,
} from './icons/SidebarCollapseIcon'
import { isPivotModeEnabled } from '../pivot/pivotMode'

type NavItem = {
  to: string
  label: string
  icon: NavIconName
  badge?: number
}

type NavGroup = {
  label?: string
  items: NavItem[]
}

const restaurantNavLegacy: NavGroup[] = [
  {
    items: [{ to: '/overview', label: 'Обзор', icon: 'overview' }],
  },
  {
    label: 'Закупки',
    items: [
      { to: '/catalog', label: 'Каталог', icon: 'catalog' },
      { to: '/my-prices', label: 'Мои прайсы', icon: 'prices' },
      { to: '/orders', label: 'Заявки', icon: 'orders' },
      { to: '/cart', label: 'Корзина', icon: 'cart' },
      { to: '/analytics', label: 'Аналитика', icon: 'analytics' },
    ],
  },
  {
    label: 'Связь',
    items: [
      { to: '/suppliers', label: 'Поставщики', icon: 'suppliers' },
      { to: '/chat', label: 'Чат', icon: 'chat' },
    ],
  },
  {
    label: 'Аккаунт',
    items: [
      { to: '/payments', label: 'Платежи', icon: 'payments' },
      { to: '/settings', label: 'Настройки', icon: 'settings' },
    ],
  },
]

/** Restaurant-analytics pivot: My Prices first, no marketplace surfaces. */
const restaurantNavPivot: NavGroup[] = [
  {
    items: [
      { to: '/my-prices', label: 'Мои прайсы', icon: 'prices' },
      { to: '/analytics', label: 'Аналитика', icon: 'analytics' },
      { to: '/cart', label: 'Заявка', icon: 'cart' },
      { to: '/payments', label: 'Платежи', icon: 'payments' },
      { to: '/settings', label: 'Настройки', icon: 'settings' },
    ],
  },
]

const supplierNavLegacy: NavGroup[] = [
  {
    items: [{ to: '/overview', label: 'Обзор', icon: 'overview' }],
  },
  {
    label: 'Работа',
    items: [
      { to: '/catalog', label: 'Каталог', icon: 'catalog' },
      { to: '/orders', label: 'Запросы', icon: 'orders' },
    ],
  },
  {
    label: 'Связь',
    items: [
      { to: '/customers', label: 'Мои клиенты', icon: 'customers' },
      { to: '/chat', label: 'Чат', icon: 'chat' },
    ],
  },
  {
    label: 'Аккаунт',
    items: [{ to: '/settings', label: 'Настройки', icon: 'settings' }],
  },
]

/** Supplier cabinet hidden in pivot — keep settings for existing accounts. */
const supplierNavPivot: NavGroup[] = [
  {
    items: [{ to: '/settings', label: 'Настройки', icon: 'settings' }],
  },
]

const navForRole = (role: UserRole | null): NavGroup[] => {
  const pivot = isPivotModeEnabled()
  const groups =
    role === 'supplier'
      ? pivot
        ? supplierNavPivot
        : supplierNavLegacy
      : pivot
        ? restaurantNavPivot
        : restaurantNavLegacy
  if (!isDemoStandEnabled()) {
    return groups
  }
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => item.to !== '/payments' && item.to !== '/settings'),
    }))
    .filter((group) => group.items.length > 0)
}

const buyerNavGateByRoute: Partial<Record<string, PlanGateFeature>> = {
  '/orders': 'platform_rfq',
  '/notifications': 'platform_rfq',
  '/chat': 'platform_chat',
  '/suppliers': 'buyer_suppliers',
  '/analytics': 'market_price_intelligence',
}

export const AppShell = () => {
  useMarketShellScrollLock()
  const { session, organizationName, role, organizations, logout, switchOrganization } = useAuth()
  const { lineCount } = useCart()
  const entitlements = useBuyerEntitlements()
  const { requestFeature, paywallModal } = usePlanGate()
  const { requestOwnerAccess, ownerPaywallModal } = useOrganizationAccess()
  const { collapsed, toggleCollapsed } = useSidebarCollapsed()
  const [navOpen, setNavOpen] = useState(false)
  const currentOrg = organizations.find((org) => org.id === session?.organizationId)
  const displayName = isDemoStandEnabled()
    ? currentOrg?.legal_name || organizationName || session?.email || ''
    : organizationName || session?.email || ''
  const email = session?.email ?? ''
  const navGroups = navForRole(role)

  const resolveBadge = (item: NavItem): number | undefined => {
    if (role === 'buyer' && item.to === '/cart' && lineCount > 0) {
      return lineCount
    }
    return item.badge
  }

  const closeNav = () => setNavOpen(false)

  const shellClass = [
    'market-shell',
    navOpen ? 'market-shell--nav-open' : '',
    collapsed ? 'market-shell--sidebar-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <RfqNotificationsProvider>
      <div className={shellClass}>
      {navOpen ? (
        <button
          type="button"
          className="market-shell__backdrop"
          aria-label="Закрыть меню"
          onClick={closeNav}
        />
      ) : null}

      <button
        type="button"
        className="market-shell__mobile-toggle"
        aria-label={navOpen ? 'Закрыть меню' : 'Открыть меню'}
        aria-expanded={navOpen}
        onClick={() => setNavOpen((open) => !open)}
      >
        <MenuIcon />
      </button>

      <aside className="market-shell__sidebar" aria-label="Навигация">
        <div className="market-shell__brand">
          <span className="market-shell__logo" title="Провизиум">
            {collapsed ? 'П' : 'Провизиум'}
          </span>
          {!collapsed ? <span className="market-shell__subtitle">Личный кабинет</span> : null}
        </div>

        <nav id="market-shell-sidebar-nav" className="market-shell__nav">
          {navGroups.map((group) => (
            <div key={group.label ?? group.items[0]?.to} className="market-shell__nav-group">
              {group.label && !collapsed ? (
                <p className="market-shell__nav-label">{group.label}</p>
              ) : null}
              {group.items.map((item) => {
                const badge = resolveBadge(item)
                const gateFeature = role === 'buyer' ? buyerNavGateByRoute[item.to] : undefined
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={item.label}
                    aria-label={item.label}
                    onClick={(event) => {
                      closeNav()
                      if (item.to === '/payments' && role === 'buyer' && !requestOwnerAccess()) {
                        event.preventDefault()
                        return
                      }
                      if (gateFeature && !requestFeature(gateFeature)) {
                        event.preventDefault()
                      }
                    }}
                    className={({ isActive }) =>
                      isActive ? 'market-shell__link market-shell__link--active' : 'market-shell__link'
                    }
                  >
                    <NavIcon name={item.icon} />
                    <span className="market-shell__link-label">{item.label}</span>
                    {badge ? <span className="market-shell__badge">{badge}</span> : null}
                  </NavLink>
                )
              })}
            </div>
          ))}
        </nav>

        <div className="market-shell__user">
          {!collapsed ? (
            <>
              <p className="market-shell__user-name">{displayName || email}</p>
              {organizations.length > 1 ? (
                <SelectCombobox
                  className="market-shell__org-switch"
                  label="Организация"
                  hideLabel
                  value={String(session?.organizationId ?? '')}
                  options={organizations.map((org) => ({
                    value: String(org.id),
                    label: org.trade_name ?? org.legal_name,
                  }))}
                  onChange={(next) => void switchOrganization(Number(next))}
                />
              ) : organizationName && !isDemoStandEnabled() ? (
                <p className="market-shell__user-org">{organizationName}</p>
              ) : null}
            </>
          ) : null}
          {isDemoStandEnabled() ? (
            <a
              className="market-shell__demo-cta"
              href={DEMO_STAND_REGISTER_URL}
              target="_blank"
              rel="noreferrer"
              title="Зарегистрироваться и получить 30 дней бесплатно"
            >
              {collapsed ? (
                <span className="market-shell__demo-cta-compact">30 дней</span>
              ) : (
                <>
                  Зарегистрироваться
                  <span className="market-shell__demo-cta-sub">
                    30 дней бесплатно
                  </span>
                </>
              )}
            </a>
          ) : null}
          <button
            type="button"
            className="market-shell__collapse-toggle"
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            aria-expanded={!collapsed}
            aria-controls="market-shell-sidebar-nav"
            title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            onClick={toggleCollapsed}
          >
            {collapsed ? <IconSidebarExpand /> : <IconSidebarCollapse />}
            <span className="market-shell__collapse-toggle-label">
              {collapsed ? 'Развернуть' : 'Свернуть'}
            </span>
          </button>
          {isDemoStandEnabled() ? null : !collapsed ? (
            <button type="button" className="echat-btn echat-btn--ghost" onClick={logout}>
              Выйти
            </button>
          ) : (
            <button
              type="button"
              className="echat-btn echat-btn--ghost market-shell__icon-only-btn"
              onClick={logout}
              title="Выйти"
              aria-label="Выйти"
            >
              <IconLogout />
            </button>
          )}
        </div>

        <button
          type="button"
          className="market-shell__edge-toggle"
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          aria-expanded={!collapsed}
          aria-controls="market-shell-sidebar-nav"
          title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          onClick={toggleCollapsed}
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      </aside>

      <main className="market-shell__main">
        <DemoUiBanner />
        <DemoStandBanner />
        {role === 'buyer' ? (
          <div className="market-shell__context-bar">
            <VenueTabs />
            {!isPivotModeEnabled() && entitlements.platformRfqEnabled ? (
              <NotificationsBellPanel />
            ) : null}
          </div>
        ) : null}
        <Outlet />
      </main>
      {paywallModal}
      {ownerPaywallModal}
      </div>
    </RfqNotificationsProvider>
  )
}
